import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "@mui/icons-material/Message";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { useLazyGetForumsQuery } from "messaging/_api/messaging-api";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import Forum from "../../forum";
import { getForumChatTitle } from "../../forum-chat-display";
import { useSession } from "users/__session/session-context";

const PAGE_SIZE = 25;

const Container = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1.5),
  width: "100%",
}));
const ResultsList = styled(List)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  maxHeight: "min(52vh, 28rem)",
  overflowY: "auto",
  padding: theme.spacing(0.5, 0),
}));
const CategoryHeader = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  fontSize: "0.75rem",
  fontWeight: 600,
  lineHeight: 1.4,
  padding: theme.spacing(1, 2, 0.5),
  position: "sticky",
  top: 0,
  zIndex: 1,
}));
const EmptyMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  textAlign: "center",
  width: "100%",
}));
const LoadingContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  color: theme.palette.text.secondary,
  display: "flex",
  gap: theme.spacing(1),
  justifyContent: "center",
  padding: theme.spacing(2),
  width: "100%",
}));
const LoadMoreContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(1),
  width: "100%",
}));

type ConversationCategory = "Conversations" | "Individuals" | "Teams" | "Proposals" | "Work";

type ConversationOption = {
  category: ConversationCategory;
  forum: Forum;
};

type ForumSelectionProps = {
  onForumSelected: (forum: Forum) => void;
};

export default function ForumSelection(props: Readonly<ForumSelectionProps>) {
  const { onForumSelected } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [forums, setForums] = useState<Forum[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [getForums] = useLazyGetForumsQuery();
  const session = useSession();

  const options = useMemo(() => forums.map(toOption), [forums]);
  const groupedOptions = groupOptions(options);
  const isInitialLoading = isLoading && pageIndex === 0;

  const loadForums = useCallback(
    async (abortController: AbortController, nextPageIndex: number, query: string) => {
      setIsLoading(true);
      try {
        // A superseded load is discarded by the `aborted` checks below rather than cancelled,
        // because the lazy trigger hands back a handle on a cache entry shared by everyone asking
        // for this query. Aborting it writes the abort error into that shared entry, and the load
        // superseding this one re-initiates the same entry in the same tick — the communications
        // page re-opens the chat dialog under a new id whenever the session object is replaced, so
        // React remounts this picker while its first request is still in flight. RTK Query then
        // sees that request still pending, skips the second thunk, and settles it from the entry
        // the abort has just failed. The picker reports "Failed to load conversations" for a
        // request nothing cancelled, and no later load re-requests it.
        const forumsRequest = getForums({
          query: new ForumQuery({
            filterParameters: new ForumFilterParameters({
              query: query.trim() || undefined,
            }),
            orderParameters: new ForumOrderParameters({
              orderBy: ForumOrderField.CreatedDate,
              orderDir: OrderDirection.Descending,
            }),
            paginationParameters: PaginationParameters.create({
              pageIndex: nextPageIndex,
              pageSize: PAGE_SIZE,
            }),
          }),
        });
        let forumsResponse;
        try {
          forumsResponse = await forumsRequest.unwrap();
        } finally {
          forumsRequest.unsubscribe();
        }
        if (abortController.signal.aborted) return;

        const loadedForums = Forum.fromJSONArray(forumsResponse) ?? [];

        setForums((currentForums) =>
          nextPageIndex === 0 ? loadedForums : mergeForums(currentForums, loadedForums),
        );
        setHasMore(loadedForums.length === PAGE_SIZE);
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error(error);
        enqueueSnackbar("Failed to load conversations", { variant: "error" });
      } finally {
        // The load that superseded this one owns the flag; clearing it here would replace that
        // load's spinner with an empty list.
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [getForums],
  );

  useEffect(() => {
    const debounceHandle = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPageIndex(0);
    }, 300);

    return () => globalThis.clearTimeout(debounceHandle);
  }, [searchTerm]);

  useEffect(() => {
    const abortController = new AbortController();

    queueMicrotask(() => {
      void loadForums(abortController, pageIndex, debouncedSearchTerm);
    });

    return () => abortController.abort();
  }, [debouncedSearchTerm, loadForums, pageIndex]);

  return (
    <Container>
      <TextField
        label="Find an existing conversation"
        placeholder="Search existing conversations"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null,
          },
        }}
      />
      <ResultsList dense>
        {isInitialLoading && (
          <ListItem disablePadding>
            <LoadingContainer>
              <CircularProgress size={20} />
              <Typography>Loading conversations</Typography>
            </LoadingContainer>
          </ListItem>
        )}
        {!isInitialLoading && options.length === 0 && (
          <ListItem disablePadding>
            <EmptyMessage>{debouncedSearchTerm ? "No matching conversations" : "No conversations"}</EmptyMessage>
          </ListItem>
        )}
        {groupedOptions.map(([category, categoryOptions]) => (
          <li key={category}>
            <CategoryHeader>{category === "Conversations" ? "Existing conversations" : category}</CategoryHeader>
            <List disablePadding dense>
              {categoryOptions.map((option) => (
                <ListItem
                  disablePadding
                  key={option.forum.id?.value ?? `${option.category}-${option.forum.name.toString()}`}
                >
                  <ListItemButton onClick={() => onForumSelected(option.forum)}>
                    <ListItemIcon>
                      <MessageIcon />
                    </ListItemIcon>
                    <ListItemText primary={getForumChatTitle(option.forum, session.user?.fullName)} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </li>
        ))}
        {!isInitialLoading && hasMore && (
          <ListItem disablePadding>
            <LoadMoreContainer>
              <Button disabled={isLoading} onClick={() => setPageIndex((currentPageIndex) => currentPageIndex + 1)}>
                {isLoading ? "Loading..." : "Load More"}
              </Button>
            </LoadMoreContainer>
          </ListItem>
        )}
      </ResultsList>
    </Container>
  );
}

function toOption(forum: Forum): ConversationOption {
  return { category: getCategory(forum), forum };
}

function getCategory(forum: Forum): ConversationCategory {
  const entityClass = forum.topic?.entityClass?.value;

  if (entityClass && ["UserNetworkConnection", "Marketplace.Individual"].includes(entityClass)) return "Individuals";
  if (entityClass === "Marketplace.Team") return "Teams";
  if (entityClass === "Work.Proposal") return "Proposals";
  if (entityClass === "Work") return "Work";

  return "Conversations";
}

function groupOptions(options: ConversationOption[]): Array<[ConversationCategory, ConversationOption[]]> {
  const grouped = new Map<ConversationCategory, ConversationOption[]>();

  for (const option of options) {
    const existingOptions = grouped.get(option.category) ?? [];
    existingOptions.push(option);
    grouped.set(option.category, existingOptions);
  }

  return [...grouped.entries()];
}

function mergeForums(currentForums: Forum[], newForums: Forum[]): Forum[] {
  const mergedForums = [...currentForums];

  for (const forum of newForums) {
    if (mergedForums.some((existingForum) => existingForum.isEqualTo(forum))) continue;
    mergedForums.push(forum);
  }

  return mergedForums;
}
